import {
  compareSearchTextMatchSortMeta,
  getSearchTextMatchSortMeta,
  isSearchKeywordEmpty,
  type SearchTextMatchPriorityOptions,
} from './getSearchTextMatchPriority';

/**
 * 두 항목의 검색 매칭 우선순위를 비교한다. `Array.prototype.sort`의 compareFn에 맞춘 부호 규칙을 따른다.
 * @description
 * - 검색어가 비어 있으면 0(동일)을 반환해 기존 순서를 유지한다.
 * - 같은 tier면 정규화 문자열에서 검색어의 `indexOf` 위치가 앞인 항목을 우선한다(`includes`에서 유효).
 * - 그다음 `getSearchText` 결과 문자열을 `localeCompare`(base)로 정렬한다.
 */
export function compareSearchTextMatchPriority<T>(
  a: T,
  b: T,
  getSearchText: (item: T) => string,
  searchKeyword: string,
  options?: SearchTextMatchPriorityOptions,
): number {
  if (isSearchKeywordEmpty(searchKeyword, options)) {
    return 0;
  }

  const ma = getSearchTextMatchSortMeta(getSearchText(a), searchKeyword, options);
  const mb = getSearchTextMatchSortMeta(getSearchText(b), searchKeyword, options);
  const byMeta = compareSearchTextMatchSortMeta(ma, mb);
  if (byMeta !== 0) return byMeta;

  return String(getSearchText(a)).localeCompare(String(getSearchText(b)), undefined, {
    sensitivity: 'base',
  });
}

/**
 * `getSearchTextMatchPriority` 기준으로 배열을 오름차순 정렬한 새 배열을 반환한다.
 * @description
 * - 검색어가 비어 있으면 순서를 바꾸지 않는다(얕은 복사).
 */
export function sortBySearchTextMatch<T>(
  items: readonly T[],
  getSearchText: (item: T) => string,
  searchKeyword: string,
  options?: SearchTextMatchPriorityOptions,
): T[] {
  if (isSearchKeywordEmpty(searchKeyword, options)) {
    return [...items];
  }
  return [...items].sort((a, b) =>
    compareSearchTextMatchPriority(a, b, getSearchText, searchKeyword, options),
  );
}
